import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import ControlPanelLayout from "./ControlPanelLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, UserRound, LogOut, Mail } from "lucide-react";

export default function CPSettings() {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) setDisplayName(data.display_name || "");
    })();
  }, [user]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim() })
      .eq("user_id", user.id);
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Profile updated");
  };

  return (
    <ControlPanelLayout>
      <div className="space-y-6 max-w-4xl">
        <section className="rounded-3xl border border-border/60 bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--primary)/0.08))] p-6 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Preferences</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight">Keep your workspace identity clean and recognizable.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            These settings shape how your hosted meetings feel to users. A clear display name and tidy session controls help the product feel trustworthy.
          </p>
        </section>

        <div className="grid gap-4 md:grid-cols-[1.1fr_0.9fr]">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserRound className="h-5 w-5 text-primary" />
                Profile identity
              </CardTitle>
              <CardDescription>Your display name is what people see across meetings and hosted experiences.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input value={user?.email || ""} disabled className="mt-2 bg-muted" />
              </div>
              <div>
                <Label>Display name</Label>
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-2" />
              </div>
              <div className="flex justify-end">
                <Button onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-primary" />
                Session controls
              </CardTitle>
              <CardDescription>Use this section when you need to leave the workspace on a shared or temporary machine.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                Signing out here ends your current session and sends you back to the public site.
              </div>
              <Button
                variant="destructive"
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = "/";
                }}
                className="w-full justify-center"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </ControlPanelLayout>
  );
}
